/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBar } from '@fluentui/react/lib/CommandBar';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { Sticky, StickyPositionType } from '@fluentui/react/lib/Sticky';
import { ISelection } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { computed, reaction, transaction } from 'mobx';
import { Observer, observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useDebounce } from 'src/core/hooks/useDebounce';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getStylist } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { FaultyIntentNotification } from 'src/luis/appShell/buildPane/intentPage/components/FaultyIntentNotification';
import { FeatureSection } from 'src/luis/appShell/buildPane/intentPage/components/FeatureSection';
import { IntentPageOpsModals } from 'src/luis/appShell/buildPane/intentPage/components/IntentPageOpsModals';
import { IntentPageSidePanelContainer } from 'src/luis/appShell/buildPane/intentPage/components/IntentPageSidePanelContainer';
import { intentPageMessages } from 'src/luis/appShell/buildPane/intentPage/intentPageMessages';
import { IntentPageUIConfigStore } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageUIConfigStore';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { BrushContainer } from 'src/luis/modules/labeling/components/BrushContainer';
import { transformLabels } from 'src/luis/modules/labeling/utils/transformLabels';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { getEntityStoreOrEntityChildStoreById, isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { EntityDropdownContainer } from 'src/luis/projects/utterances/components/EntityDropdownContainer';
import { UtteranceTable } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceTable';
import { UtteranceTableCommandBar } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceTableCommandBar/UtteranceTableCommandBar';
import { ModelIdentifier } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { usePaginatedUtterances } from 'src/luis/projects/utterances/hooks/usePaginatedUtterances';
import { ConfirmPredictionActionSource, useUtteranceActions } from 'src/luis/projects/utterances/hooks/useUtteranceActions';
import { useUtterances } from 'src/luis/projects/utterances/hooks/useUtterances';
import { useUtterancesWithAnalytics } from 'src/luis/projects/utterances/hooks/useUtterancesWithAnalytics';
import { UtteranceTableViewOptions } from 'src/luis/projects/utterances/types/UtteranceTableViewOptions';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';
import {
    applyDifferenceFilter,
    applyEntityFilter,
    applyNearestRivalFilter,
    applyScoreFilter
} from 'src/luis/projects/utterances/utils/filterUtils';
import { utteranceDenormalizer } from 'src/luis/projects/utterances/utils/utteranceDenormalizer';
import { isMLPrediction } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { LuisId } from 'src/luis/types/Resource';
import { FiltersPillContainer } from 'src/verticals/utterancesFilters/components/FiltersPillContainer';
import { SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

const { styleComponent, styleDiv, getClassName } = getStylist('IntentPage');
const ScrollablePaneWrapper = styleDiv('ScrollablePaneWrapper', { flex: 1, position: 'relative' });
const ScrollablePaneContent = styleComponent<IStackProps>(Stack)('ScrollablePaneContent', { overflow: 'hidden', padding: 32 });
const stickyHeaderClassName = getClassName('StickyHeader', theme => ({ boxShadow: theme.commandBarBoxShadow }));

export const getSelectedIds = (selection: ISelection) => (selection.getSelection() as UtteranceObservable[]).map(({ id }) => id);

export const IntentPage = observer((props: ModelIdentifier) => {
    const { model } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { announcedComponent, announceAction } = useAnnounced();

    const pageRef = React.useRef<HTMLDivElement>();
    const activitySource = 'IntentPageBase';
    const eventLoggerRef = React.useRef(getEventLogger(activitySource));

    const { isRTL } = rootStore.currentLuisAppStore;
    const { analyticsStore } = rootStore.currentLuisAppStore.currentAppVersionStore;
    const { entityStoreHouse, intentStoreHouse, phraseListStoreHouse } = rootStore.currentLuisAppStore.currentAppVersionStore;

    const [pageConfigsStore] = React.useState<IntentPageUIConfigStore>(() => new IntentPageUIConfigStore());
    const commandBarRef = React.useRef<ICommandBar>();

    const { intentFiltersStore, sidePaneEntitySelectionStore, labelingStore, opsStore } = pageConfigsStore;
    const { brushStore } = labelingStore;

    const debouncedQuery = useDebounce(pageConfigsStore.searchQuery, 1000);

    const isChunking = Boolean(debouncedQuery.trim()) || intentFiltersStore.isFilterModeOn;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const {
        reassignIntent,
        submitUtterance,
        confirmEntityPredictions,
        editUtterance,
        changePageIndex,
        addLabel,
        addSynonymToSublist,
        confirmPrediction,
        createSublist,
        removeLabels
    } = useUtteranceActions({ model });

    const {
        areModelsLoaded,
        entities,
        intents,
        isLoading,
        pageIndex,
        totalSize,
        pageSize,
        isLoadingChunks,
        utterances: utterancesWithoutAnalytics
    } = useUtterances({ model, isChunking });

    const { utterances: utterancesWithAnalytics } = useUtterancesWithAnalytics({
        model,
        isLoading,
        utterances: utterancesWithoutAnalytics
    });

    const filteredUtterances = React.useMemo(() => {
        const orderedUtterances = orderBy(
            utterancesWithAnalytics,
            ({ utteranceStore }: UtteranceWithAnalytics) => utteranceStore.createdDate,
            ['desc']
        );

        return substringSearchStrategy(
            orderedUtterances,
            debouncedQuery,
            null,
            (utterance, q) => utterance.utteranceStore.text.toLocaleLowerCase().indexOf(q) !== -1
        ).filter(
            u =>
                applyEntityFilter(intentFiltersStore.appliedEntitiesFilter, u) &&
                applyNearestRivalFilter(intentFiltersStore.appliedIntentsFilter, u, intents) &&
                applyDifferenceFilter(intentFiltersStore.appliedDifferenceFilter, u, analyticsStore.lastTrainingDate) &&
                applyScoreFilter(intentFiltersStore.scoreWindow, u)
        );
    }, [
        utterancesWithAnalytics,
        intentFiltersStore.appliedEntitiesFilter,
        intentFiltersStore.appliedIntentsFilter,
        intentFiltersStore.appliedDifferenceFilter,
        intentFiltersStore.scoreWindow,
        debouncedQuery
    ]);

    const { items: pageItems, pageCount, currentPage, onPageChange } = usePaginatedUtterances({
        utterances: filteredUtterances,
        isChunking,
        changePageIndex,
        pageIndex,
        pageSize,
        totalSize
    });

    const utterancesObservable = React.useMemo(
        () =>
            pageItems.map(utterancePrediction => ({
                id: utterancePrediction.utteranceStore.id,
                utteranceWithAnalytics: utterancePrediction,
                computed: computed(() => {
                    const denormalizedUtterance = utteranceDenormalizer({
                        utterancePrediction,
                        intentStores: intentStoreHouse.items,
                        entityStores: entityStoreHouse.items,
                        phraseListStores: phraseListStoreHouse.items
                    });

                    return [
                        denormalizedUtterance,
                        transformLabels({
                            tokenizedText: denormalizedUtterance.tokenizedText,
                            userLabels: denormalizedUtterance.labeledEntities,
                            predictions: denormalizedUtterance.predictedEntities
                        })
                    ] as const;
                })
            })),
        [pageItems, intentStoreHouse, entityStoreHouse, phraseListStoreHouse]
    );

    const hasUnconfirmedPredictions = computed(() =>
        opsStore.selectedItems.reduce((acc, item) => acc || item.computed.get()[1].labels.some(isMLPrediction), false)
    ).get();

    const isAddAsPatternDisabled = computed(() => {
        if (opsStore.selectedItems.length !== 1 || !entityStoreHouse.items.length) {
            return true;
        }

        const { utteranceWithAnalytics } = opsStore.selectedItems[0];
        if (utteranceWithAnalytics.utteranceStore.labeledEntities.length > 0) {
            return false;
        }
        if (utteranceWithAnalytics.predictionStore?.predictedEntities.length > 0) {
            return utteranceWithAnalytics.predictionStore.predictedEntities.some(e => {
                const foundEntity = getEntityStoreOrEntityChildStoreById(e.entityId, entityStoreHouse.items);

                return foundEntity ? isMachineLearned(foundEntity.typeId) : false;
            });
        }

        return true;
    }).get();

    const currentIntent = React.useMemo(() => intents.find(intent => intent.id === model.id), [intents, model.id]);
    const intentsWithoutCurrentIntent = React.useMemo(() => intents.filter(intent => intent.id !== model.id), [intents, model.id]);
    const isTableLoading = isLoading || (isLoadingChunks && !utterancesObservable.length);

    React.useEffect(() => {
        if (areModelsLoaded) {
            const activeDifferenceFilters = history.location.state ? (history.location.state as PassedFilters).differenceFilters : [];
            const activeNearestRivalIds = history.location.state
                ? (history.location.state as PassedFilters).nearestRivalIntentsFilters
                : [];

            initializeFilters(activeDifferenceFilters, activeNearestRivalIds);
        }
    }, [areModelsLoaded, intents.length, entities.length]);

    useDocumentEventHandler(
        'keyup',
        React.useCallback(e => {
            if (e.key === LuisKeyCodes.Escape) {
                sidePaneEntitySelectionStore.resetSelection();
            }
        }, [])
    );

    React.useEffect(
        () =>
            reaction(
                () => sidePaneEntitySelectionStore.selection,
                entitySelection => {
                    if (entitySelection?.entityId) {
                        labelingStore.setLabelingMode('brush');
                    } else {
                        labelingStore.setLabelingMode('menu');
                    }
                }
            ),

        [entities]
    );

    const editUtteranceTracker = useProgressTracker({
        onComplete: () => {
            transaction(() => {
                announceAction(localize(intentPageMessages.editUtteranceAccessibilityConfirmation));
                pageConfigsStore.selection.setAllSelected(false);
                opsStore.resetModalsConfigs();
                opsStore.setSelectedUtteranceId(null);
                opsStore.setIsUtteranceEditModeOn(false);
            });
        }
    });

    const reassignUtteranceTracker = useProgressTracker({
        onComplete: () => {
            transaction(() => {
                announceAction(localize(intentPageMessages.reassignUtteranceAccessibilityConfirmation));
                pageConfigsStore.selection.setAllSelected(false);
                opsStore.resetModalsConfigs();
            });
        }
    });

    const updateViewOptions = React.useCallback(
        (utterancesViewOption: Partial<UtteranceTableViewOptions>) =>
            dispatcher.dispatch('toggleUtterancesViewOption', { utterancesViewOption }),
        [dispatcher.dispatch]
    );

    const initializeFilters = React.useCallback(
        (activeDifferenceFiltersInit: ('correct' | 'incorrect' | 'unclear')[], activeNearestRivalIds: LuisId[]) => {
            if (activeDifferenceFiltersInit.length) {
                updateViewOptions({ hideIntentDetailedScores: false });
            }

            intentFiltersStore.initializeFiltersValues(intents, entities, activeDifferenceFiltersInit, activeNearestRivalIds);
        },
        [intents, entities, intentFiltersStore]
    );

    const handleFiltersChange = React.useCallback(
        (change: SelectionChange) => {
            if (change.changeType === 'difference') {
                updateViewOptions({ hideIntentDetailedScores: false });
            }
            intentFiltersStore.changeFilter(change);
        },
        [intentFiltersStore]
    );

    const handleRenameIntent = React.useCallback(
        (name: string) =>
            dispatcher.dispatch('renameIntent', {
                id: model.id,
                name,
                toastData: {
                    running: { title: localize(intentPageMessages.renameIntentToastStart) },
                    completed: { title: localize(intentPageMessages.renameIntentToastSuccess) },
                    failed: { title: localize(intentPageMessages.renameIntentToastFailed) }
                }
            }),
        [dispatcher.dispatch, model]
    );

    const handleReassignIntent = React.useCallback(
        (destIntentId: string, selectedUtteranceId?: number) =>
            reassignIntent(
                selectedUtteranceId ? [selectedUtteranceId] : getSelectedIds(pageConfigsStore.selection),
                destIntentId,
                reassignUtteranceTracker
            ),
        [reassignIntent, pageConfigsStore]
    );

    const handleConfirmPredictions = React.useCallback(
        (actionSource: ConfirmPredictionActionSource, selectedUtteranceId?: number) =>
            confirmEntityPredictions(
                selectedUtteranceId ? [selectedUtteranceId] : getSelectedIds(pageConfigsStore.selection),
                actionSource
            ),
        [confirmEntityPredictions, pageConfigsStore, model]
    );

    const handleToggleEntityPane = React.useCallback(() => {
        transaction(() => {
            pageConfigsStore.selection.setAllSelected(false);

            if (pageConfigsStore.activeBladeType === 'entityPalette') {
                pageConfigsStore.setActiveBladeType('none');
                sidePaneEntitySelectionStore.resetSelection();
            } else {
                pageConfigsStore.setActiveBladeType('entityPalette');
            }
        });
    }, [pageConfigsStore, sidePaneEntitySelectionStore]);

    const handleToggleFilters = React.useCallback(() => {
        if (pageConfigsStore.activeBladeType === 'viewOptions') {
            pageConfigsStore.setActiveBladeType('none');
        } else {
            pageConfigsStore.setActiveBladeType('viewOptions');
        }
    }, [pageConfigsStore]);

    const handleCreateNewEntity = React.useCallback(
        (name: string) => {
            transaction(() => {
                opsStore.setNewEntityCreationName(name);
                opsStore.setModalsConfigState({ isEntityModalOpen: true });
                eventLoggerRef.current.start('CreateEntityDialog');
            });
        },
        [opsStore]
    );

    const handleQueryChange = React.useCallback(
        (searchQuery: string) => {
            eventLoggerRef.current.log('UtteranceTableSearchTriggered');
            pageConfigsStore.setSearchQuery(searchQuery);
        },
        [pageConfigsStore]
    );

    const handleEditUtteranceSubmit = React.useCallback(
        (text: string) => {
            const utteranceDoesNotHaveLabels =
                utterancesWithoutAnalytics.find(utterance => utterance.utteranceStore.id === opsStore.selectedUtteranceId).utteranceStore
                    .labeledEntities.length === 0;

            if (uiStore.isEditUtteranceDisclaimerModalHidden || utteranceDoesNotHaveLabels) {
                editUtterance(opsStore.selectedUtteranceId, text, editUtteranceTracker);
            } else {
                opsStore.setModalsConfigState({ isEditUtteranceModalOpen: true });
            }
        },
        [utterancesWithoutAnalytics, opsStore]
    );

    const handleDeleteUtterance = React.useCallback(() => opsStore.setModalsConfigState({ isDeleteModalOpen: true }), []);

    const handleEditUtterance = React.useCallback(() => {
        opsStore.setIsUtteranceEditModeOn(true);
        opsStore.setSelectedUtteranceId(opsStore.selectedItems[0].id);
    }, []);

    const handleAddAsPattern = React.useCallback(() => opsStore.setModalsConfigState({ isAddToPatternsModalOpen: true }), []);

    const onDeleteUtterance = (utteranceId: number) => {
        opsStore.setSelectedUtteranceId(utteranceId);
        opsStore.setModalsConfigState({ isDeleteModalOpen: true });
    };

    const onEditUtterance = (utteranceId: number) => {
        opsStore.setIsUtteranceEditModeOn(true);
        opsStore.setSelectedUtteranceId(utteranceId);
    };

    const onSidePanelClose = React.useCallback(() => {
        commandBarRef.current.focus();
        pageConfigsStore.setActiveBladeType('none');
        pageConfigsStore.labelingStore.setLabelingMode('menu');
    }, []);

    return (
        <Page
            reactToZoomLevel={false}
            pageName="IntentPageBase"
            title={localize(intentPageMessages.intentPageTitle, { intent: currentIntent && currentIntent.name })}>
            <Stack grow horizontal>
                <ScrollablePaneWrapper originalRef={pageRef}>
                    <ScrollablePane>
                        <ScrollablePaneContent tokens={{ childrenGap: 40 }}>
                            <Stack horizontal>
                                <EditableTitle
                                    isLoading={!currentIntent}
                                    title={currentIntent?.name}
                                    onUpdateTitle={handleRenameIntent}
                                    titleTextProps={{ fontWeight: 600 }}
                                    isEditable={currentIntent && currentIntent.name !== LuisConstants.Luis.NoneIntentName && canWrite}
                                />

                                <FaultyIntentNotification
                                    isIncorrectFilterActive={intentFiltersStore.isIncorrectFilterActive}
                                    isUnclearFilterActive={intentFiltersStore.isUnclearFilterActive}
                                    intentId={currentIntent ? currentIntent.id : ''}
                                    onApplyFilters={filters => intentFiltersStore.applyDifferenceFilters(filters)}
                                />
                            </Stack>
                            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}

                            {currentIntent && <FeatureSection featurePickerProps={{ model: currentIntent }} />}

                            <Stack>
                                <Stack horizontal verticalAlign="center">
                                    <EditableTitle
                                        descriptionAlignment="center"
                                        titleTextProps={{ fontWeight: 600, size: '18px' }}
                                        title={localize(intentPageMessages.examplesTitle)}
                                        description={localize(intentPageMessages.examplesDescription)}
                                    />
                                    {isLoadingChunks && <Spinner styles={{ root: { marginBottom: '8px' } }} />}
                                </Stack>

                                <Sticky stickyClassName={stickyHeaderClassName} stickyPosition={StickyPositionType.Header}>
                                    <UtteranceTableCommandBar
                                        userRole={userRole}
                                        onEdit={handleEditUtterance}
                                        componentRef={commandBarRef}
                                        onDelete={handleDeleteUtterance}
                                        onQueryChange={handleQueryChange}
                                        onAddToPatterns={handleAddAsPattern}
                                        query={pageConfigsStore.searchQuery}
                                        displayedUserRole={displayedUserRole}
                                        onMoveToIntent={handleReassignIntent}
                                        onChangeViewOptions={updateViewOptions}
                                        onToggleFiltersPane={handleToggleFilters}
                                        viewOptions={uiStore.utterancesViewOptions}
                                        onToggleEntityPalette={handleToggleEntityPane}
                                        selectedView={pageConfigsStore.activeBladeType}
                                        isAddToPatternsDisabled={isAddAsPatternDisabled}
                                        reAssignableIntents={intentsWithoutCurrentIntent}
                                        selectedItemsLength={opsStore.selectedItems.length}
                                        isConfirmAllPredictionsDisabled={!hasUnconfirmedPredictions}
                                        areFiltersApplied={intentFiltersStore.areFilterItemsApplied}
                                        onConfirmEntityPredictions={() => handleConfirmPredictions('commandBar')}
                                    />
                                </Sticky>

                                {intentFiltersStore.areFilterItemsApplied && (
                                    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5, padding: '24px 0' }}>
                                        <LuisText>{localize(intentPageMessages.filtersApplied)}</LuisText>

                                        <FiltersPillContainer
                                            filters={intentFiltersStore.appliedFilters}
                                            onRemoveFilter={handleFiltersChange}
                                        />
                                    </Stack>
                                )}

                                <UtteranceTable
                                    model={model}
                                    isRTL={isRTL}
                                    userRole={userRole}
                                    onEdit={onEditUtterance}
                                    isLoading={isTableLoading}
                                    items={utterancesObservable}
                                    onDelete={onDeleteUtterance}
                                    labelingStore={labelingStore}
                                    onInputSubmit={submitUtterance}
                                    displayedUserRole={displayedUserRole}
                                    selection={pageConfigsStore.selection}
                                    onReassignIntent={handleReassignIntent}
                                    viewOptions={uiStore.utterancesViewOptions}
                                    onSubmitEditInput={handleEditUtteranceSubmit}
                                    overallItemsCount={filteredUtterances.length}
                                    isAddAsPatternDisabled={isAddAsPatternDisabled}
                                    reAssignableIntents={intentsWithoutCurrentIntent}
                                    lastTrainingDate={analyticsStore.lastTrainingDate}
                                    sidePaneEntitySelection={sidePaneEntitySelectionStore.selection}
                                    onDismissEditInput={() => opsStore.setIsUtteranceEditModeOn(false)}
                                    utteranceIdBeingEdited={opsStore.isUtteranceEditModeOn && opsStore.selectedUtteranceId}
                                    onAddToPatterns={() => opsStore.setModalsConfigState({ isAddToPatternsModalOpen: true })}
                                    onConfirmPrediction={utteranceId => handleConfirmPredictions('utteranceRow', utteranceId)}
                                    onDeleteKeyUp={() => canDeleteData && opsStore.setModalsConfigState({ isDeleteModalOpen: true })}
                                    areItemsFiltered={Boolean(pageConfigsStore.searchQuery || intentFiltersStore.appliedFilters.length)}
                                />
                                {announcedComponent}

                                <TablePagination
                                    pageCount={pageCount}
                                    currentPage={currentPage}
                                    onPageChange={p => onPageChange(p.selected)}
                                />
                            </Stack>
                        </ScrollablePaneContent>
                    </ScrollablePane>

                    <Observer>
                        {() => (
                            <EntityDropdownContainer
                                entities={entities}
                                utteranceLabels={
                                    utterancesObservable.find(u => u.id === labelingStore.menuStore.currentUtteranceId)?.computed.get()[1]
                                        .labels || []
                                }
                                menuStore={labelingStore.menuStore}
                                tokenSelectionMetadata={labelingStore.tokenSelectionMetadata}
                                onSetTokenSelectionMetadata={React.useCallback(data => labelingStore.setTokenSelectionMetadata(data), [])}
                                isEntityPaneOpen={pageConfigsStore.activeBladeType === 'entityPalette'}
                                onOpenEntityPane={handleToggleEntityPane}
                                actions={{
                                    onAddLabel: addLabel,
                                    onAddSynonymToSublist: addSynonymToSublist,
                                    onConfirmPrediction: confirmPrediction,
                                    onCreateSublist: createSublist,
                                    onRemoveLabels: removeLabels,
                                    onCreateEntity: handleCreateNewEntity
                                }}
                            />
                        )}
                    </Observer>

                    <BrushContainer
                        attachedToRef={pageRef}
                        brushStore={brushStore}
                        labelingMode={labelingStore.mode}
                        sidePaneEntitySelectionStore={sidePaneEntitySelectionStore}
                    />
                </ScrollablePaneWrapper>

                <IntentPageSidePanelContainer
                    model={model}
                    onPanelClose={onSidePanelClose}
                    pageConfigsStore={pageConfigsStore}
                    onFiltersChange={handleFiltersChange}
                    utterancesCount={filteredUtterances.length}
                />
            </Stack>

            <IntentPageOpsModals
                model={model}
                entities={entities}
                activitySource={activitySource}
                pageConfigsStore={pageConfigsStore}
                utteranceStores={utterancesWithAnalytics}
            />
        </Page>
    );
});
